// no-dd-sa:typescript-best-practices/no-unused-expressions
import { useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Lock,
  Mail,
  PlugZap,
  Quote,
  ShieldCheck,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { logError } from '@/lib/logger';
import { trackCtaClick } from '@/lib/analytics';
import LandingLayout from '@/components/layout/LandingLayout';
import { SEO } from '@/components/common/SEO';
import { api } from '@/lib/api/core';
import { marketingApi } from '@/lib/api/marketing';
import NetworkGraph from '@/components/charts/NetworkGraph';
import { MOCK_REVIEW_PAIRS } from '@/data/sampleReportData';
import LeadCaptureModal from '@/components/landing/LeadCaptureModal';
import { useInView } from '@/hooks/useInView';
import { useBfcacheRestore } from '@/hooks/useBfcacheRestore';

const trustBadges = [
  { icon: ShieldCheck, label: 'SOC 2-aligned controls' },
  { icon: Lock, label: 'Read-only GitHub access' },
  { icon: CheckCircle2, label: 'We never read your files' },
];

const caseStudyStats = [
  {
    value: '77%',
    label: 'faster PR cycle time',
    detail: '6.1 to 1.4 days - NexGen Technologies, 85 engineers',
  },
  {
    value: '+340%',
    label: 'deploy frequency',
    detail: '2.3 to 11.1 deploys/week - NexGen Technologies',
  },
  {
    value: '-77%',
    label: 'change failure rate',
    detail: '18% to 4.2% - Datastream Analytics, 35 engineers',
  },
];

const testimonials = [
  {
    quote:
      "The thing that sold me was our first planning meeting after connecting it. I'd been telling the CTO we were slow because of tech debt - turns out almost a third of our PRs were just sitting there waiting on the same two reviewers. We redistributed reviews and the difference was noticeable within a couple weeks.",
    author: 'Robert Striker',
    title: 'VP of Engineering',
    company: 'Article Fiesta',
    result: 'Found the review bottleneck in the first planning meeting',
  },
  {
    quote:
      "Our retros used to be... kind of a blame game? Everyone had a different theory about where time went and nobody had receipts. Now I just pull up the dashboard and it's right there. We found out review turnaround was way worse than anyone thought, like almost three days on average. Got it down to same-day once we actually saw it.",
    author: 'Jonathon Turner',
    title: 'Engineering Manager',
    company: 'Ultra Communications',
    result: 'Review turnaround: almost 3 days to same-day',
  },
];

const proofStats = [
  { label: 'Setup', value: '5 min', detail: 'GitHub OAuth to your first dashboard' },
  { label: 'Board-Ready Deck', value: '1 click', detail: 'export your executive summary as slides' },
  { label: 'Pricing', value: 'Flat', detail: "one org price - hiring doesn't raise your bill" },
];

const personaBenefits = [
  {
    title: 'For VPs & Directors',
    headline: 'Prove ROI and defend headcount.',
    bullets: [
      'Automatic DORA roll-ups tied to business initiatives.',
      'Hotspot alerts that quantify risk by revenue impact.',
      'Executive-ready briefings you can export to the board in 2 clicks.',
    ],
  },
  {
    title: 'For Engineering Managers',
    headline: 'Keep delivery predictable without micromanaging.',
    bullets: [
      'Review-network map that highlights overloaded seniors.',
      'Silo + turnover alerts so you can pair or rotate proactively.',
      'Sprint health pack that shows PR size, depth, and stuck work.',
    ],
  },
];

const differentiators = [
  {
    icon: TrendingUp,
    title: 'Outcome-first metrics',
    copy: 'Track cycle time, quality, and capacity against targets the CFO already cares about.',
  },
  {
    icon: Target,
    title: 'Precision hotspot detection',
    copy: 'Surface the 5% of files driving 50% of incidents with contextual owners.',
  },
  {
    icon: BarChart3,
    title: 'Narrative-ready reporting',
    copy: 'Auto-generate a board slide with change failure rate, initiatives, and next bets.',
  },
  {
    icon: Users,
    title: 'Healthy team insights',
    copy: 'Spot review load, silos, and burnout signals before the attrition conversation.',
  },
];

const processSteps = [
  {
    step: '01',
    title: 'Connect',
    copy: 'Authorize GitHub (read-only) or invite us to a sandbox org. No agents or local installs.',
  },
  {
    step: '02',
    title: 'Sync',
    copy: 'Quick sync surfaces your first insights in minutes; the full six months of history lands within the hour.',
  },
  {
    step: '03',
    title: 'Brief',
    copy: 'Your executive health scorecard, hotspots, and review map are ready for your next staff.',
  },
];

const securityPoints = [
  'Read-only GitHub access - we cannot push, merge, or modify anything.',
  'We read PR, commit and review metadata. Your code stays yours - we never clone your repository or read your files. The only code we store is the short snippet GitHub attaches to an inline review comment.',
  'Encrypted at rest, TLS 1.2+ in transit, and every query scoped to your organization.',
  'GitHub tokens encrypted with Fernet before storage.',
];

export default function EngineeringCodeIntelligencePage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [setupLinkEmail, setSetupLinkEmail] = useState('');
  const [setupLinkStatus, setSetupLinkStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // Un-stick "Preparing GitHub connect..." when the user taps Back from GitHub
  useBfcacheRestore(() => setIsLoading(false));

  // Animation refs for scroll-triggered animations
  const [heroRef, heroInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [dashboardRef, dashboardInView] = useInView<HTMLDivElement>({ threshold: 0.2 });
  const [logosRef, logosInView] = useInView<HTMLDivElement>({ threshold: 0.2 });
  const [testimonialsRef, testimonialsInView] = useInView<HTMLDivElement>({ threshold: 0.15 });
  const [personaRef, personaInView] = useInView<HTMLDivElement>({ threshold: 0.15 });
  const [differentiatorRef, differentiatorInView] = useInView<HTMLDivElement>({ threshold: 0.15 });
  const [howItWorksRef, howItWorksInView] = useInView<HTMLDivElement>({ threshold: 0.15 });
  const [securityRef, securityInView] = useInView<HTMLDivElement>({ threshold: 0.15 });
  const [networkRef, networkInView] = useInView<HTMLDivElement>({ threshold: 0.15 });
  const [ctaRef, ctaInView] = useInView<HTMLDivElement>({ threshold: 0.2 });

  // Mobile fallback: ~85% of paid clicks are phones, where the GitHub App
  // install flow is painful. Capture the lead instead (goes to Slack for a
  // founder follow-up with the setup link).
  const handleSetupLinkSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!setupLinkEmail || setupLinkStatus === 'submitting') return;
    setSetupLinkStatus('submitting');
    trackCtaClick({ location: 'engineering_code_intelligence_setup_link' });
    try {
      await marketingApi.submitMarketingLead({
        // Backend LeadCreateRequest requires a non-empty name; the email local
        // part is the best identifier we have from a single-field mobile form.
        name: setupLinkEmail.split('@')[0] || 'Mobile visitor',
        email: setupLinkEmail,
        notes: 'Requested setup link from mobile hero (engineering-code-intelligence)',
        source_url: typeof window !== 'undefined' ? window.location.href : '',
        allow_contact: true,
      });
      setSetupLinkStatus('success');
    } catch (error) {
      logError('Failed to submit setup-link request:', error);
      setSetupLinkStatus('error');
    }
  };

  const handleGitHubLogin = async () => {
    trackCtaClick({ location: 'engineering_code_intelligence' });
    try {
      setIsLoading(true);
      const { authorization_url } = await api.getGitHubAuthUrl();
      window.location.href = authorization_url;
    } catch (error) {
      logError('Failed to get GitHub auth URL:', error);
      setIsLoading(false);
    }
  };

  return (
    <LandingLayout>
      <SEO />
      <section className="bg-gradient-to-b from-gray-950 via-gray-900 to-gray-800 text-white pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.25),_transparent_55%)]" aria-hidden="true" />
        <div ref={heroRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className={`text-sm uppercase tracking-[0.25em] text-primary-200 mb-6 ${heroInView ? 'animate-hero-fade-in' : 'opacity-0'}`}>
            Engineering Intelligence for 50-500 dev orgs
          </p>
          <h1 className={`text-4xl md:text-6xl font-bold leading-tight mb-6 max-w-3xl ${heroInView ? 'animate-hero-fade-in animation-delay-100' : 'opacity-0'}`}>
            Stop defending headcount without proof.
          </h1>
          <p className={`text-xl text-gray-200 max-w-3xl mb-8 ${heroInView ? 'animate-hero-fade-in animation-delay-200' : 'opacity-0'}`}>
            CodePulse turns raw GitHub activity into ROI-ready narratives so your executive team can see which initiatives deserve runway, where review work is stuck, and how healthy your delivery engine really is.
          </p>

          <div className={`flex flex-col gap-3 mb-6 ${heroInView ? 'animate-fade-in-up animation-delay-300' : 'opacity-0'}`}>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleGitHubLogin}
                disabled={isLoading}
                className="inline-flex flex-1 items-center justify-center rounded-xl bg-primary-400 px-8 py-4 text-lg font-bold text-gray-950 shadow-lg shadow-primary-500/40 transition hover:bg-primary-300 disabled:opacity-60 animate-cta-pulse"
              >
                {isLoading ? 'Preparing GitHub connect…' : 'Connect GitHub instantly'}
              </button>
              <button
                type="button"
                onClick={() => setIsLeadModalOpen(true)}
                className="inline-flex flex-1 items-center justify-center rounded-xl border border-white/40 px-8 py-4 text-lg font-semibold text-white transition hover:bg-white/10"
              >
                <Mail className="mr-2 h-5 w-5" />
                Book executive briefing
              </button>
            </div>

            {/* Mobile fallback: capture a lead instead of pushing the GitHub App install on a phone */}
            {setupLinkStatus === 'success' ? (
              <p className="sm:hidden rounded-xl border border-primary-300/40 bg-primary-500/10 px-4 py-3 text-sm text-primary-100">
                Check your inbox - we'll send your setup link and a 2-minute walkthrough.
              </p>
            ) : (
              <form className="sm:hidden" onSubmit={handleSetupLinkSubmit}>
                <p className="text-sm text-gray-300 mb-2">
                  On your phone? We'll email you a setup link for when you're at your desk.
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    required
                    value={setupLinkEmail}
                    onChange={(event) => setSetupLinkEmail(event.target.value)}
                    placeholder="Work email"
                    aria-label="Work email"
                    className="min-w-0 flex-1 rounded-lg border border-white/20 bg-white/10 px-3 py-3 text-white placeholder-gray-400 focus:border-primary-300 focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={setupLinkStatus === 'submitting'}
                    className="rounded-lg border border-white/30 bg-white/15 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/25 disabled:opacity-60"
                  >
                    Email me the link
                  </button>
                </div>
                {setupLinkStatus === 'error' && (
                  <p className="mt-2 text-sm text-red-300">
                    Something went wrong - try again or email hello@codepulsehq.com.
                  </p>
                )}
              </form>
            )}
          </div>

          <div className={`text-sm text-gray-300 mb-12 space-y-1 ${heroInView ? 'animate-fade-in-up animation-delay-400' : 'opacity-0'}`}>
            <p>No SDR hoops. Choose the self-serve audit or send procurement to a guided briefing.</p>
            <div className="flex flex-wrap gap-3 text-xs uppercase tracking-[0.3em] text-white/70">
              <span>Live in 5 minutes</span>
              <span>No agent installs</span>
              <span>Flat pricing - no per-seat math</span>
            </div>
          </div>

          <div className={`flex flex-wrap gap-6 text-sm text-gray-200 ${heroInView ? 'animate-fade-in-up animation-delay-500' : 'opacity-0'}`}>
            {trustBadges.map((badge, index) => (
              <div
                key={badge.label}
                className={`flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 ${heroInView ? `animate-scale-in animation-delay-${(index + 5) * 100}` : 'opacity-0'}`}
              >
                <badge.icon className="w-4 h-4 text-primary-300" />
                <span>{badge.label}</span>
              </div>
            ))}
          </div>
          <div className={`mt-10 grid gap-4 md:grid-cols-2 ${heroInView ? 'animate-fade-in-up animation-delay-600' : 'opacity-0'}`}>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-5 text-white shadow-lg shadow-primary-900/20">
              <div className="flex items-center gap-3">
                <PlugZap className="h-6 w-6 text-primary-300" />
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-primary-200">Self-serve track</p>
                  <p className="text-lg font-semibold">Connect GitHub in under 3 minutes</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-gray-300">
                Read-only OAuth - choose your repos and get this same board-ready report without booking a call.
              </p>
            </div>
            <div className="rounded-3xl border border-primary-200/30 bg-primary-500/10 p-5 text-white shadow-lg shadow-primary-900/30">
              <div className="flex items-center gap-3">
                <CalendarDays className="h-6 w-6 text-primary-200" />
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-primary-200">Guided briefing</p>
                  <p className="text-lg font-semibold">Loop in exec staff & security</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-primary-100">
                We send our security documentation, prep your agenda, and tailor the walkthrough to whatever budget or risk conversation is next.
              </p>
            </div>
          </div>
        </div>

        <div ref={dashboardRef} className="mt-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`bg-gray-900/70 border border-white/10 rounded-2xl shadow-2xl backdrop-blur ${dashboardInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <img
              src="/landing_pages/executive-dashboard.webp"
              alt="Executive engineering health dashboard"
              className="w-full h-auto rounded-2xl"
              loading="lazy"
            />
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              {proofStats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={`rounded-xl bg-white/5 border border-white/10 p-4 ${dashboardInView ? `animate-scale-in animation-delay-${(index + 2) * 100}` : 'opacity-0'}`}
                >
                  <div className="text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm uppercase tracking-wide text-gray-400 mt-1">{stat.label}</div>
                  <p className="text-xs text-gray-400 mt-2">{stat.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white dark:bg-gray-950 border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={logosRef}>
            <p className={`text-sm uppercase tracking-[0.3em] text-primary-600 dark:text-primary-400 text-center mb-6 ${logosInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
              Results from documented case studies
            </p>
            <div className={`grid sm:grid-cols-3 gap-6 ${logosInView ? 'animate-fade-in-up animation-delay-150' : 'opacity-0'}`}>
              {caseStudyStats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={`text-center rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 ${logosInView ? `animate-scale-in animation-delay-${(index + 2) * 100}` : 'opacity-0'}`}
                >
                  <div className="text-4xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                  <div className="text-sm font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400 mt-1">{stat.label}</div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{stat.detail}</p>
                </div>
              ))}
            </div>
          </div>

          <div ref={testimonialsRef} className="grid md:grid-cols-2 gap-6 mt-12 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.author}
                className={`rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm ${testimonialsInView ? `animate-fade-in-up animation-delay-${index * 150}` : 'opacity-0'}`}
              >
                <Quote className="w-10 h-10 text-primary-500 mb-4" />
                <p className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  "{testimonial.quote}"
                </p>
                <div className="text-sm text-primary-600 dark:text-primary-300 font-semibold">
                  {testimonial.result}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-3">
                  {testimonial.author} - {testimonial.title}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{testimonial.company}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white dark:bg-gray-950">
        <div ref={personaRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-14 ${personaInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <p className="text-sm uppercase tracking-[0.3em] text-primary-600 dark:text-primary-400 mb-3">Tailored Proof</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">Every persona gets the story they need</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {personaBenefits.map((persona, index) => (
              <div
                key={persona.title}
                className={`rounded-2xl border border-gray-200 dark:border-gray-800 p-8 bg-white dark:bg-gray-900 shadow-lg shadow-gray-900/5 ${personaInView ? `animate-fade-in-up animation-delay-${(index + 1) * 200}` : 'opacity-0'}`}
              >
                <p className="text-sm uppercase text-primary-600 dark:text-primary-400 font-semibold mb-2">{persona.title}</p>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">{persona.headline}</h3>
                <ul className="space-y-3 text-gray-600 dark:text-gray-300">
                  {persona.bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div ref={differentiatorRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 ${differentiatorInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <p className="text-sm uppercase tracking-[0.3em] text-primary-600 dark:text-primary-400 mb-3">Why CodePulse</p>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Metrics that move budget conversations forward</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {differentiators.map((item, index) => (
              <div
                key={item.title}
                className={`rounded-2xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-6 flex gap-4 ${differentiatorInView ? `animate-fade-in-up animation-delay-${(index + 1) * 150}` : 'opacity-0'}`}
              >
                <div className="w-12 h-12 rounded-xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-300">
                  <item.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{item.copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white dark:bg-gray-950">
        <div ref={howItWorksRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className={`text-sm uppercase tracking-[0.3em] text-primary-600 dark:text-primary-400 mb-3 ${howItWorksInView ? 'animate-fade-in-up' : 'opacity-0'}`}>How it works</p>
              <h2 className={`text-3xl font-bold text-gray-900 dark:text-white mb-6 ${howItWorksInView ? 'animate-fade-in-up animation-delay-100' : 'opacity-0'}`}>From OAuth to board deck in three steps</h2>
              <div className="space-y-6">
                {processSteps.map((step, index) => (
                  <div
                    key={step.step}
                    className={`flex gap-4 ${howItWorksInView ? `animate-fade-in-up animation-delay-${(index + 2) * 150}` : 'opacity-0'}`}
                  >
                    <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">{step.step}</div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{step.title}</h3>
                      <p className="text-gray-600 dark:text-gray-300">{step.copy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={`rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-8 space-y-6 ${howItWorksInView ? 'animate-fade-in-up animation-delay-300' : 'opacity-0'}`}>
              <div className="flex items-center gap-4">
                <Zap className="w-10 h-10 text-amber-500" />
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">Snapshot</p>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Engineering Health Score</h3>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={`rounded-xl bg-white dark:bg-gray-800 p-4 border border-gray-100 dark:border-gray-700 ${howItWorksInView ? 'animate-scale-in animation-delay-400' : 'opacity-0'}`}>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Velocity</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">Elite</p>
                  <span className="text-xs text-primary-500">50th to 85th percentile</span>
                </div>
                <div className={`rounded-xl bg-white dark:bg-gray-800 p-4 border border-gray-100 dark:border-gray-700 ${howItWorksInView ? 'animate-scale-in animation-delay-500' : 'opacity-0'}`}>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Quality</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">99.1%</p>
                  <span className="text-xs text-primary-500">Checks passing</span>
                </div>
                <div className={`rounded-xl bg-white dark:bg-gray-800 p-4 border border-gray-100 dark:border-gray-700 ${howItWorksInView ? 'animate-scale-in animation-delay-600' : 'opacity-0'}`}>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Collaboration</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">3.6</p>
                  <span className="text-xs text-primary-500">Avg review depth</span>
                </div>
                <div className={`rounded-xl bg-white dark:bg-gray-800 p-4 border border-gray-100 dark:border-gray-700 ${howItWorksInView ? 'animate-scale-in animation-delay-700' : 'opacity-0'}`}>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Risk</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">5 files</p>
                  <span className="text-xs text-primary-500">own 48% of churn</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div ref={securityRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 space-y-6">
              <p className={`text-sm uppercase tracking-[0.3em] text-primary-600 dark:text-primary-400 ${securityInView ? 'animate-fade-in-up' : 'opacity-0'}`}>Security & Compliance</p>
              <h2 className={`text-3xl font-bold text-gray-900 dark:text-white ${securityInView ? 'animate-fade-in-up animation-delay-100' : 'opacity-0'}`}>Enterprise guardrails baked in</h2>
              <p className={`text-lg text-gray-600 dark:text-gray-300 ${securityInView ? 'animate-fade-in-up animation-delay-200' : 'opacity-0'}`}>
                Security reviews shouldn't slow your growth targets. We bring the receipts up front so procurement, legal, and your security champion stay unblocked.
              </p>
              <ul className="space-y-4 text-gray-600 dark:text-gray-300">
                {securityPoints.map((point, index) => (
                  <li
                    key={point}
                    className={`flex gap-3 ${securityInView ? `animate-fade-in-up animation-delay-${(index + 3) * 100}` : 'opacity-0'}`}
                  >
                    <ShieldCheck className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={`order-1 lg:order-2 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-8 space-y-6 ${securityInView ? 'animate-fade-in-up animation-delay-150' : 'opacity-0'}`}>
              <div className="flex items-center gap-3">
                <Lock className="w-6 h-6 text-primary-500" />
                <p className="text-lg font-semibold text-gray-900 dark:text-white">Security Brief Download</p>
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                Share this one-pager with your security partner. It covers our data flow, controls, retention, and incident response.
              </p>
              <a href="/security" className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 font-semibold hover:underline">
                View full security posture
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white dark:bg-gray-950">
        <div ref={networkRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className={`text-sm uppercase tracking-[0.3em] text-primary-600 dark:text-primary-400 ${networkInView ? 'animate-fade-in-up' : 'opacity-0'}`}>Spot bottlenecks</p>
              <h2 className={`text-3xl font-bold text-gray-900 dark:text-white ${networkInView ? 'animate-fade-in-up animation-delay-100' : 'opacity-0'}`}>See who is blocking whom before sprint review</h2>
              <p className={`text-gray-600 dark:text-gray-300 ${networkInView ? 'animate-fade-in-up animation-delay-200' : 'opacity-0'}`}>
                Review load maps show exactly where senior reviewers are overloaded, which PRs are aging out, and where you can pair mid-level ICs to keep momentum without micromanagement.
              </p>
              <div className={`grid sm:grid-cols-2 gap-4 ${networkInView ? 'animate-fade-in-up animation-delay-300' : 'opacity-0'}`}>
                <div className={`border border-gray-200 dark:border-gray-800 rounded-xl p-4 ${networkInView ? 'animate-scale-in animation-delay-400' : 'opacity-0'}`}>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aging PRs flagged</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">14</p>
                  <span className="text-xs text-amber-500 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Need reviewer rebalancing
                  </span>
                </div>
                <div className={`border border-gray-200 dark:border-gray-800 rounded-xl p-4 ${networkInView ? 'animate-scale-in animation-delay-500' : 'opacity-0'}`}>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Overloaded reviewers</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">3</p>
                  <span className="text-xs text-primary-500">Auto-suggest coverage swaps</span>
                </div>
              </div>
            </div>
            <div className={`rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-4 ${networkInView ? 'animate-fade-in-up animation-delay-200' : 'opacity-0'}`}>
              <NetworkGraph pairs={MOCK_REVIEW_PAIRS} height={360} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary-700 text-white">
        <div ref={ctaRef} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className={`text-sm uppercase tracking-[0.3em] text-white/70 mb-3 ${ctaInView ? 'animate-fade-in-up' : 'opacity-0'}`}>Next step</p>
          <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${ctaInView ? 'animate-fade-in-up animation-delay-100' : 'opacity-0'}`}>Bring objective engineering health data to your next executive meeting</h2>
          <p className={`text-lg text-white/80 mb-10 ${ctaInView ? 'animate-fade-in-up animation-delay-200' : 'opacity-0'}`}>
            Get a custom Engineering Health Score plus a prioritized action plan for your org in minutes, not weeks.
          </p>
          <div className={`flex flex-col sm:flex-row gap-4 justify-center ${ctaInView ? 'animate-fade-in-up animation-delay-300' : 'opacity-0'}`}>
            <button
              type="button"
              onClick={() => setIsLeadModalOpen(true)}
              className="inline-flex items-center justify-center rounded-lg bg-white text-primary-700 font-semibold px-8 py-4 shadow-lg hover:bg-primary-50 transition animate-cta-pulse"
            >
              Schedule Briefing
            </button>
            <a
              href="/sample-report"
              className="inline-flex items-center justify-center rounded-lg border border-white/60 px-8 py-4 font-semibold text-white hover:bg-white/10 transition"
            >
              Download Sample Report
            </a>
          </div>
          <p className={`text-sm text-white/70 mt-6 ${ctaInView ? 'animate-fade-in-up animation-delay-400' : 'opacity-0'}`}>
            Need security docs first?{' '}
            <a href="/security" className="underline">
              View the trust center
            </a>
            .
          </p>
        </div>
      </section>
      <LeadCaptureModal open={isLeadModalOpen} onClose={() => setIsLeadModalOpen(false)} onConnectNow={handleGitHubLogin} />
    </LandingLayout>
  );
}
